import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  X,
  ArrowRight,
  ArrowLeft,
  QrCode,
  Inbox,
  UserPlus,
  Clock,
  ArrowUpRight,
  MessageSquarePlus,
  CheckCircle2,
} from "lucide-react";

const STORAGE_KEY_PREFIX = "doctor_onboarding_completed_";

const STEPS = [
  {
    id: "smart-link",
    target: '[data-testid="smart-link-btn"]',
    icon: QrCode,
    title: "Tu Smart Link personal",
    body: "Acá generás un enlace único que compartís con tus pacientes (por WhatsApp, email o como QR). Cuando lo usan, ya entran a tu bandeja sin pasos extra.",
    placement: "bottom",
  },
  {
    id: "inbox",
    target: '[data-testid="consults-grid"]',
    icon: Inbox,
    title: "Tu bandeja de consultas",
    body: "Acá ves todas las consultas que te llegaron, ordenadas por prioridad. Las urgentes aparecen primero, con un indicador visible para que sepas dónde mirar.",
    placement: "top",
  },
  {
    id: "invite",
    target: '[data-testid="invite-colleague-btn"]',
    icon: UserPlus,
    title: "Invitar a un colega",
    body: "Si querés sumar a un colega a la plataforma, lo invitás desde acá. Le llega un mail con instrucciones para registrarse y solicitar acceso.",
    placement: "bottom",
  },
  {
    id: "sla",
    target: '[data-testid="response-time-config-btn"]',
    icon: Clock,
    title: "Tu tiempo de respuesta",
    body: "Configurá en cuánto tiempo te comprometés a responder. Si te vas de vacaciones o estás en una conferencia, podés activar una ausencia temporal y el sistema le avisa al paciente antes de que cargue la consulta.",
    placement: "bottom",
  },
  {
    id: "escalate",
    target: null,
    icon: ArrowUpRight,
    title: "¿Qué es la función Escalar?",
    body: "Cuando una consulta no es apropiada para el formato asincrónico (porque requiere examinar al paciente, es urgente o necesita una conversación en tiempo real), podés escalar la consulta a atención presencial, teleconsulta o emergencia. El paciente recibe un aviso claro con el motivo y queda registrado para auditoría.",
    placement: null,
  },
  {
    id: "request-info",
    target: null,
    icon: MessageSquarePlus,
    title: "Solicitar más información",
    body: "Si una consulta llega incompleta (te falta un dato, una foto o un estudio), podés pedirle más información al paciente sin responder formalmente. Tenés un plazo de 7 días para que el paciente complete; mientras tanto, no se le cobra nada (el cobro recién corre cuando enviás la respuesta final).",
    placement: null,
  },
];

const TOOLTIP_MAX_WIDTH = 360;
const TOOLTIP_SIDE_MARGIN = 12;
const TOOLTIP_GAP = 16;
const VIEWPORT_MARGIN = 20;

// Effective tooltip width: never wider than the viewport minus side margins,
// so the box can't overflow the screen edges on small/mobile screens.
function getTooltipWidth() {
  return Math.min(TOOLTIP_MAX_WIDTH, window.innerWidth - TOOLTIP_SIDE_MARGIN * 2);
}

function computeTooltipPosition(targetEl, placement, tooltipHeight) {
  if (!targetEl) return null;
  const rect = targetEl.getBoundingClientRect();
  const scrollY = window.scrollY;
  const scrollX = window.scrollX;
  const viewportW = window.innerWidth;
  const width = getTooltipWidth();

  let top;
  if (placement === "top") {
    // Position tooltip so its BOTTOM is GAP px above the target's top
    top = rect.top + scrollY - TOOLTIP_GAP - tooltipHeight;
  } else {
    top = rect.bottom + scrollY + TOOLTIP_GAP;
  }

  let left = rect.left + scrollX + rect.width / 2 - width / 2;
  // Clamp to viewport horizontally
  const minLeft = TOOLTIP_SIDE_MARGIN;
  const maxLeft = Math.max(minLeft, viewportW - width - TOOLTIP_SIDE_MARGIN);
  if (left < minLeft) left = minLeft;
  if (left > maxLeft) left = maxLeft;

  return { top, left };
}

function ensureTooltipInViewport(top, tooltipHeight) {
  const scrollY = window.scrollY;
  const viewportH = window.innerHeight;
  const tooltipTopInViewport = top - scrollY;
  const tooltipBottomInViewport = tooltipTopInViewport + tooltipHeight;

  if (tooltipTopInViewport < VIEWPORT_MARGIN) {
    window.scrollBy({
      top: tooltipTopInViewport - VIEWPORT_MARGIN,
      behavior: "smooth",
    });
  } else if (tooltipBottomInViewport > viewportH - VIEWPORT_MARGIN) {
    window.scrollBy({
      top: tooltipBottomInViewport - (viewportH - VIEWPORT_MARGIN),
      behavior: "smooth",
    });
  }
}

function highlightTarget(targetEl) {
  if (!targetEl) return () => {};
  const prevPosition = targetEl.style.position;
  const prevZIndex = targetEl.style.zIndex;
  const prevBoxShadow = targetEl.style.boxShadow;
  const prevBorderRadius = targetEl.style.borderRadius;
  const prevTransition = targetEl.style.transition;
  targetEl.style.position = targetEl.style.position || "relative";
  targetEl.style.zIndex = "51";
  targetEl.style.boxShadow = "0 0 0 4px rgba(59, 130, 246, 0.4), 0 0 0 8px rgba(59, 130, 246, 0.15)";
  targetEl.style.borderRadius = targetEl.style.borderRadius || "8px";
  targetEl.style.transition = "box-shadow 0.25s ease";
  return () => {
    targetEl.style.position = prevPosition;
    targetEl.style.zIndex = prevZIndex;
    targetEl.style.boxShadow = prevBoxShadow;
    targetEl.style.borderRadius = prevBorderRadius;
    targetEl.style.transition = prevTransition;
  };
}

export default function DoctorOnboardingTour({ user, onComplete }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [position, setPosition] = useState(null);
  const [targetEl, setTargetEl] = useState(null);
  // When an anchored step's target isn't on the page (e.g. a brand-new doctor has
  // no consults, so [data-testid="consults-grid"] doesn't exist), fall back to a
  // centered box instead of rendering nothing (which left only the blurred backdrop).
  const [renderCentered, setRenderCentered] = useState(false);
  const tooltipRef = useRef(null);
  const step = STEPS[stepIndex];
  const isCenter = !step.target;
  const centered = isCenter || renderCentered;
  const totalSteps = STEPS.length;

  // Resolve target element on every step + window resize
  useLayoutEffect(() => {
    if (isCenter) {
      setTargetEl(null);
      setPosition(null);
      setRenderCentered(false);
      return undefined;
    }

    setRenderCentered(false);

    const tryResolve = (isFinal) => {
      const el = document.querySelector(step.target);
      if (!el) {
        // Target not found. On the final attempt, show the step centered so it's
        // never invisible and the doctor can always continue the tour.
        if (isFinal) {
          setTargetEl(null);
          setPosition(null);
          setRenderCentered(true);
        }
        return;
      }
      setRenderCentered(false);
      setTargetEl(el);
      // Scroll target into view first (centered vertically)
      const rect = el.getBoundingClientRect();
      const outOfView = rect.top < 0 || rect.bottom > window.innerHeight;
      if (outOfView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      // Compute preliminary position using measured tooltip height (or fallback)
      const tooltipHeight = tooltipRef.current?.offsetHeight || 240;
      const pos = computeTooltipPosition(el, step.placement, tooltipHeight);
      setPosition(pos);
    };

    tryResolve(false);
    const t = setTimeout(() => tryResolve(true), 150); // retry once for late-mounting elements

    const onResize = () => {
      const el = document.querySelector(step.target);
      if (!el) return;
      const tooltipHeight = tooltipRef.current?.offsetHeight || 240;
      setPosition(computeTooltipPosition(el, step.placement, tooltipHeight));
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);

    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [stepIndex, isCenter, step.target, step.placement]);

  // After tooltip is rendered/measured, recompute using real height and ensure viewport
  useLayoutEffect(() => {
    if (isCenter || !targetEl || !tooltipRef.current) return;
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const pos = computeTooltipPosition(targetEl, step.placement, tooltipHeight);
    if (pos && (position?.top !== pos.top || position?.left !== pos.left)) {
      setPosition(pos);
    }
    if (pos) ensureTooltipInViewport(pos.top, tooltipHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetEl, stepIndex]);

  // Add highlight box-shadow to the target element while it's active
  useEffect(() => {
    if (!targetEl) return undefined;
    return highlightTarget(targetEl);
  }, [targetEl]);

  const finish = () => {
    try {
      if (user?.id) {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${user.id}`, "1");
      }
    } catch {
      // ignore
    }
    if (onComplete) onComplete();
  };

  const next = () => {
    if (stepIndex < totalSteps - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      finish();
    }
  };

  const back = () => {
    if (stepIndex > 0) setStepIndex(stepIndex - 1);
  };

  const Icon = step.icon;

  return (
    <div
      className="fixed inset-0 z-50"
      data-testid="doctor-onboarding-tour"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/55 backdrop-blur-[1px]" onClick={finish} />

      {/* Tooltip */}
      {(position || centered) && (
        <div
          ref={tooltipRef}
          className={`absolute bg-white rounded-xl shadow-2xl border border-slate-200 p-5 ${
            centered ? "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" : ""
          }`}
          style={
            centered
              ? { width: getTooltipWidth() }
              : { top: position.top, left: position.left, width: getTooltipWidth() }
          }
          data-testid={`tour-step-${step.id}`}
        >
          {/* Close X */}
          <button
            onClick={finish}
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 transition-colors"
            data-testid="tour-skip-btn"
            aria-label="Saltar tour"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Header */}
          <div className="flex items-start gap-3 mb-3">
            <div className="h-9 w-9 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon className="h-5 w-5 text-blue-600" />
            </div>
            <div className="flex-1 pr-6">
              <p className="text-xs text-slate-400 mb-0.5">Paso {stepIndex + 1} de {totalSteps}</p>
              <h3 className="text-base font-semibold text-slate-900">{step.title}</h3>
            </div>
          </div>

          {/* Body */}
          <p className="text-sm text-slate-700 leading-relaxed mb-5">{step.body}</p>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={finish}
              className="text-xs text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline"
              data-testid="tour-skip-link"
            >
              Saltar tour
            </button>

            <div className="flex items-center gap-2">
              {stepIndex > 0 && (
                <Button variant="outline" size="sm" onClick={back} data-testid="tour-back-btn">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Atrás
                </Button>
              )}
              <Button
                size="sm"
                onClick={next}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="tour-next-btn"
              >
                {stepIndex === totalSteps - 1 ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Listo
                  </>
                ) : (
                  <>
                    Siguiente
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Progress dots */}
          <div className="mt-4 flex items-center justify-center gap-1.5">
            {STEPS.map((s, i) => (
              <span
                key={s.id}
                className={`h-1.5 rounded-full transition-all ${
                  i === stepIndex ? "w-6 bg-blue-600" : "w-1.5 bg-slate-300"
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function shouldShowDoctorOnboarding(user) {
  if (!user?.id) return false;
  if (user.role !== "MEDICO" && user.role !== "ADMIN") return false;
  try {
    return localStorage.getItem(`${STORAGE_KEY_PREFIX}${user.id}`) !== "1";
  } catch {
    return false;
  }
}

export function resetDoctorOnboarding(userId) {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${userId}`);
  } catch {
    // ignore
  }
}
